/**
 * "Is this supplier's price good?" — answered from the replies already on the screen.
 *
 * ---- Why not from purchase history ----
 *
 * The obvious source is what this item has cost before, and `items.get_purchase_history` is meant
 * to say. It answers `[]` for every item on this bench regardless of arguments (recorded in
 * `api/useSuppliers.ts`), so a screen built on it would show nothing to anybody.
 *
 * The replies themselves are real data, already loaded, and answer the question a purchaser is
 * actually asking while reading them: of everyone who quoted THIS item for THIS enquiry, is the
 * one I am looking at the cheap one or the dear one? That is the comparison that decides an
 * allocation, and it needs no endpoint at all.
 *
 * When purchase history starts returning rows, "against their own last quote" becomes a second,
 * complementary signal — `ItemHistoryModal`'s `priceChangePct` already computes it.
 */

import type { Money } from './types'

export interface BenchmarkRow {
  item_code: string
  rate: Money
  supplier: string
}

export interface Benchmark {
  /** The lowest rate anyone quoted for this item on this enquiry. */
  best: Money
  bestSupplier: string
  /** How many suppliers quoted it — a "best" of one is not a comparison. */
  quotedBy: number
}

/**
 * Cheapest quote per item across every reply received.
 *
 * Rows priced at or below zero are ignored: `parse_supplier_reply` emits rate `0.00` for a
 * "sorry, no stock" reply (documented in `SupplierQuotationsPage`), and letting that win would
 * make every real quote look infinitely expensive against a supplier who declined.
 *
 * Mixed currencies are NOT converted — comparing them would need an FX rate this has no business
 * inventing. An item quoted in two currencies is left out entirely rather than compared wrongly.
 */
export function bestRateByItem(rows: readonly BenchmarkRow[]): Map<string, Benchmark> {
  const byItem = new Map<string, BenchmarkRow[]>()
  for (const row of rows) {
    if (!row.item_code || row.rate.amount <= 0) continue
    const list = byItem.get(row.item_code) ?? []
    list.push(row)
    byItem.set(row.item_code, list)
  }

  const out = new Map<string, Benchmark>()
  for (const [item, quotes] of byItem) {
    const currencies = new Set(quotes.map((q) => q.rate.currency))
    if (currencies.size > 1) continue
    const cheapest = quotes.reduce((low, q) => (q.rate.amount < low.rate.amount ? q : low))
    out.set(item, { best: cheapest.rate, bestSupplier: cheapest.supplier, quotedBy: quotes.length })
  }
  return out
}

/**
 * How far above the best quote this one sits, as a whole percentage.
 *
 * `null` — meaning "say nothing" — when this IS the best, when only one supplier quoted (there is
 * nothing to be above), or when the currencies differ. A badge reading "0% above" on the cheapest
 * quote is noise; one reading "18% above" on the only quote is a lie.
 */
export function premiumOverBest(rate: Money, benchmark: Benchmark | undefined): number | null {
  if (!benchmark || benchmark.quotedBy < 2) return null
  if (rate.currency !== benchmark.best.currency) return null
  if (rate.amount <= benchmark.best.amount || benchmark.best.amount <= 0) return null
  return Math.round(((rate.amount - benchmark.best.amount) / benchmark.best.amount) * 100)
}
